import React from 'react';
import getName from '../../../utils/getName';
import * as Card from '../../v2BaseComponents/Card';
import { getIntl } from '../../../utils/intl';
import urls from '../../../utils/urls';
import useCareTeamMember from '../../../hooks/api/useCareTeamMember';
import { CareTeamMemberDetails } from '../../../primary-record-types/apiTypes/medicalProfileService';
import { isSupport, isProvider, isInsurance, getCareTeamMemberDescriptor } from '../../../utils/careTeamUtils';
import { getFallbackForGenericString, getFallbackFromName } from '../../v2BaseComponents/Avatar';
import { isPresent } from 'ts-is-present';
import { supportTypeSelectOptions } from '../CareTeamForm/utils';

const intl = getIntl('CareTeamMemberCard', {
    support: 'Support',
    emergency: 'Emergency',
    insurance: 'Insurance',
});

export type CareTeamMemberCardOwnProps = {
    medicalProfileId: string;
    careTeamMember: CareTeamMemberDetails;
    onPress?: () => void;
    isDisabled?: boolean;
};

export type CareTeamMemberCardProps = CareTeamMemberCardOwnProps;

const CareTeamMemberCard: React.FC<CareTeamMemberCardProps> = ({
    careTeamMember,
    medicalProfileId,
    onPress,
    isDisabled,
}) => {
    const careTeamMemberDescriptor = getCareTeamMemberDescriptor(careTeamMember);
    const getHeaderText = () => {
        if (careTeamMemberDescriptor.type === 'text') {
            return careTeamMemberDescriptor.text;
        } else {
            return getName(careTeamMemberDescriptor);
        }
    };

    const getChipText = () => {
        if (isSupport(careTeamMember)) {
            return intl('support');
        } else if (isInsurance(careTeamMember)) {
            return intl('insurance');
        } else {
            return careTeamMember.metadata.providerType;
        }
    };

    const getDetailsText = () => {
        if (isSupport(careTeamMember)) {
            return <Card.CardBody2>{careTeamMember.contactDetails.description}</Card.CardBody2>;
        } else if (isInsurance(careTeamMember) || isProvider(careTeamMember)) {
            return (
                <Card.CardBody2>
                    {isInsurance(careTeamMember)
                        ? careTeamMember.metadata.memberId
                        : careTeamMember.contactDetails.description}
                </Card.CardBody2>
            );
        }
    };

    const getAvatarFallbackText = () => {
        if (careTeamMemberDescriptor.type === 'name') {
            return getFallbackFromName(careTeamMemberDescriptor.firstName, careTeamMemberDescriptor.lastName);
        } else {
            return getFallbackForGenericString(careTeamMemberDescriptor.text);
        }
    };

    const getTagText = () => {
        if (isSupport(careTeamMember)) {
            return careTeamMember.contactDetails.role;
        } else if (isInsurance(careTeamMember)) {
            return careTeamMember.metadata.insuranceRole;
        } else {
            return careTeamMember.metadata.providerSpecialty;
        }
    };
    const getSupportTypeSecondChip = () => {
        if (!isSupport(careTeamMember)) return null;
        const supportTypeChipTitle = supportTypeSelectOptions.find(
            (typeOption) => careTeamMember.metadata.supportCareTeamMemberType === typeOption.value,
        )?.label;
        return supportTypeChipTitle
            ? {
                  title: supportTypeChipTitle,
                  isSecondaryChip: true,
              }
            : null;
    };

    return (
        <Card.Card onPress={onPress} isDisabled={isDisabled} testID="care-team-member-card">
            <Card.CardLeftContent
                leftContent={
                    <Card.CardLoadableAvatar
                        size="md"
                        fallbackName={getAvatarFallbackText()}
                        avatarUrl={urls.api.careTeamMembers.avatar(medicalProfileId, careTeamMember.careTeamMemberId)}
                    />
                }
            >
                <Card.CardContent>
                    <Card.CardHeader>{getHeaderText()}</Card.CardHeader>
                    <Card.CardInlineContent>
                        <Card.CardChipSet
                            chips={[
                                getTagText()
                                    ? {
                                          title: getTagText()!,
                                      }
                                    : null,
                                {
                                    title: getChipText(),
                                },
                                getSupportTypeSecondChip(),
                            ].filter(isPresent)}
                        />
                        <Card.CardBody2>{careTeamMember.contactDetails.phoneNumber}</Card.CardBody2>
                    </Card.CardInlineContent>
                    {getDetailsText()}
                </Card.CardContent>
            </Card.CardLeftContent>
        </Card.Card>
    );
};

export default React.memo(CareTeamMemberCard);

export type UnloadedCareTeamMemberCardProps = Omit<CareTeamMemberCardProps, 'careTeamMember'> & {
    careTeamMemberId: string;
    skeletonFallback?: boolean;
};

export const UnloadedCareTeamMemberCard: React.FC<UnloadedCareTeamMemberCardProps> = (props) => {
    const { careTeamMember, isLoadingCareTeamMember } = useCareTeamMember({
        medicalProfileId: props.medicalProfileId,
        careTeamMemberId: props.careTeamMemberId,
    });

    if (isLoadingCareTeamMember || !careTeamMember) {
        return props.skeletonFallback ? <Card.CardSkeleton /> : null;
    }

    return <CareTeamMemberCard {...props} careTeamMember={careTeamMember} />;
};
